import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useNode } from '../contexts/NodeContext';
import {
  getNodeCameras, getNodeGroups, getNodeGroupsForFilter, deleteCamera, getNodeModelAssignments,
  getRecordingConfigs, bulkUpdateRecordingConfig,
  bulkSaveNodeModelAssignments, addCamerasToGroup, removeCameraFromGroup,
} from '../services/api.service';
import { TrashIcon } from '@heroicons/react/24/solid';
import LiveStreamPlayer from '../components/LiveStreamPlayer';
import { CameraTree, CameraListTable, ListPager, CameraModelTags, Tooltip, BulkRecordingModeModal, LatencyBadge, usePeerLatency, CameraModelsModal, CameraGroupsModal, type PeerLatency } from '@trinetra/shared';
import {
  MagnifyingGlassIcon,
  CheckIcon,
  Squares2X2Icon,
  ListBulletIcon,
  PlusIcon,
  ArrowUpTrayIcon,
  XMarkIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  VideoCameraIcon,
  FolderIcon,
  FolderOpenIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FunnelIcon,
  Cog6ToothIcon,
  PencilIcon,
  EllipsisVerticalIcon,
  EyeIcon,
  FilmIcon,
  BugAntIcon,
} from '@heroicons/react/24/outline';
import { useNodeStream } from '../contexts/NodeStreamContext';
import DebugOverlay from '../components/DebugOverlay';
import { Activity } from 'lucide-react';
import CameraFormModal from '../components/modals/CameraFormModal';
import {
  isDebugMode,
  setDebugMode,
  subscribeDebugMode,
  attachCloudMonitor,
  stopCloudMonitor,
  getCameraStats,
  subscribeStats,
} from '../services/cloud-stats-collector';
import { PacketMonitorPanel } from '@trinetra/shared';
// Importing for the side effect: binds the cloud stats collector into the
// shared monitor before any panel can start a run.
import '../services/cloud-packet-monitor';
import { useAuth } from '../contexts/AuthContext';
import { canUseDebugTools } from '../utils/permissions';
import { createPortal } from 'react-dom';



const GRID_LAYOUTS = [
  { label: '2×2', cols: 2, key: '2x2' },
  { label: '3×3', cols: 3, key: '3x3' },
  { label: '4×4', cols: 4, key: '4x4' },
  { label: '5×5', cols: 5, key: '5x5' },
  { label: '6×6', cols: 6, key: '6x6' },
  { label: '8×8', cols: 8, key: '8x8' },
];

// Limit cameras rendered per page to avoid browser overload with 1000+ cameras
const PAGE_SIZE_GRID = 9; // grid: limit concurrent WebRTC streams
const PAGE_SIZE_LIST = 50; // list/tree: can show more since no video rendering

/**
 * Two nav surfaces share this page:
 *   mode="live"   → Cameras ▸ Camera List (grid of WebRTC streams)
 *   mode="manage" → Camera Management ▸ Edit Cameras (table/tree, no streams)
 * The management surface never enters grid mode, so it subscribes to no streams.
 */
/**
 * camera id → the NAMES of every model that will run on it, direct and group-inherited.
 *
 * Module scope because two callers need it now — the page's first load and the reload after a
 * row-level assignment — and a second copy of this walk is a second place for the two to
 * disagree about what a camera runs.
 */
function buildCameraModelMap(d: any, cams: any[]): Record<string, string[]> {
  const modelNameById: Record<string, string> = {};
  (d?.models || []).forEach((m: any) => { modelNameById[m.id] = m.name; });
  const gAssign = d?.group_assignments || [];
  const cAssign = d?.camera_assignments || [];
  const memberships = d?.camera_group_memberships || [];

  const groupModels: Record<string, Set<string>> = {};
  for (const a of gAssign) { if (!groupModels[a.group_id]) groupModels[a.group_id] = new Set(); groupModels[a.group_id].add(a.model_id); }
  const camGroups: Record<string, string[]> = {};
  for (const m of memberships) { if (!camGroups[m.camera_id]) camGroups[m.camera_id] = []; camGroups[m.camera_id].push(m.group_id); }
  // No memberships in the payload at all → fall back to whatever the camera rows carry.
  if (memberships.length === 0) { cams.forEach((c: any) => { if (c.groups) camGroups[c.id] = c.groups.map((g: any) => g.id || g); }); }

  const map: Record<string, string[]> = {};
  for (const cam of cams) {
    const modelIds = new Set<string>();
    for (const gid of (camGroups[cam.id] || [])) { const mods = groupModels[gid]; if (mods) for (const mid of mods) modelIds.add(mid); }
    for (const a of cAssign) { if (a.camera_id === cam.id) modelIds.add(a.model_id); }
    if (modelIds.size > 0) map[cam.id] = [...modelIds].map((id) => modelNameById[id] || id);
  }
  return map;
}

export default function CamerasPage({ mode = 'live' }: { mode?: 'live' | 'manage' }) {
  const manageMode = mode === 'manage';
  const { selectedNode } = useNode();
  const navigate = useNavigate();

  const { hasPermission, user } = useAuth();
  const canAdd = hasPermission('cameras.add');
  const canEdit = hasPermission('cameras.edit');
  const canDelete = hasPermission('cameras.delete');
  const canConfig = hasPermission('cameras.config');
  const canDebug = canUseDebugTools(user);
  const canConfigRecording = hasPermission('recordings.config');
  // The two row-level assignment actions, each gated on the SAME capability its full page uses
  // — `ai-models.assign` (ModelAssignmentPage) and `groups.assign` (GroupCameraMapPage). NOTE
  // the cloud's permission names are NOT the node's: the node spells these `models.assign` and
  // `groups.assign_cameras`, and the cloud proxy translates by gating its own route with its
  // own name. Using the node's spelling here would hide both actions from every cloud user.
  const canAssignModels = hasPermission('ai-models.assign');
  const canAssignGroups = hasPermission('groups.assign');
  // Selection drives BOTH bulk delete and bulk recording settings. Gating it on
  // cameras.delete alone left recordings.config users unable to select anything, which made
  // the Recording Settings action permanently unreachable for them.
  const canBulkSelect = canDelete || canConfigRecording;

  const [cameras, setCameras] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [cameraModelMap, setCameraModelMap] = useState<Record<string, string[]>>({});
  /** The assignments response, raw — see the note where it is set. */
  const [assignData, setAssignData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  // Group filter: a LIST, because a camera list is routinely "these two floors" rather than
  // one group. Holds group objects so the toolbar can label the chips without re-resolving
  // them against the tree.
  const [selectedGroups, setSelectedGroups] = useState<any[]>([]);
  const selectedGroupIds = useMemo(() => new Set(selectedGroups.map((g: any) => g.id)), [selectedGroups]);
  // Sent to the node as group_id_recursive. A string, not the array, so it can be an effect
  // dependency without re-firing on every render.
  const groupFilterParam = selectedGroups.map((g: any) => g.id).join(',');
  /** Add or remove one group, keeping the rest — what a click in the picker means. */
  const toggleGroup = useCallback((g: any) => {
    if (!g?.id) return;
    setSelectedGroups((prev: any[]) =>
      prev.some((x) => x.id === g.id) ? prev.filter((x) => x.id !== g.id) : [...prev, g]);
  }, []);
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'tree'>(manageMode ? 'list' : 'grid');
  const [editingCamera, setEditingCamera] = useState<any>(null);

  // Flatten group tree for GroupPickerPanel
  const flatGroups = useMemo(() => {
    function flatten(nodes: any[], level = 0, parent_id: string | null = null): any[] {
      let result: any[] = [];
      for (const node of nodes) {
        result.push({ id: node.id, name: node.name, level, parent_id, camera_count: node.camera_count });
        if (node.children && node.children.length) {
          result = result.concat(flatten(node.children, level + 1, node.id));
        }
      }
      return result;
    }
    return flatten(groups);
  }, [groups]);
  const [gridLayout, setGridLayout] = useState('3x3');
  const [showAdd, setShowAdd] = useState(false);
  const [selectedCameraForEnlarged, setSelectedCameraForEnlarged] = useState<any>(null);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [showGridDropdown, setShowGridDropdown] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedCameraIds, setSelectedCameraIds] = useState<Set<string>>(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Bulk recording-mode editor — mirrors the local dashboard.
  const [showBulkRecording, setShowBulkRecording] = useState(false);
  const [recordingMeta, setRecordingMeta] = useState<{ defaults: any; options: any }>({ defaults: {}, options: {} });

  // Live streams over the shared, session-level connection (no per-page WS/PC).
  const { subscribe, unsubscribe, streams: allStreams, states, manager } = useNodeStream();
  const nodeId = selectedNode?.nodeId;
  const nodeName = selectedNode?.name ?? '';

  // Per-camera recovery wiring → shared manager.
  const handleCameraStall = useCallback((cameraId: string) => {
    if (nodeId) manager()?.handleCameraStall(nodeId, cameraId);
  }, [nodeId, manager]);

  const handleCameraPlaying = useCallback((cameraId: string) => {
    if (nodeId) manager()?.handleCameraPlaying(nodeId, cameraId);
  }, [nodeId, manager]);

  const handleCameraRetry = useCallback((cameraId: string) => {
    if (nodeId) manager()?.retryCamera(nodeId, cameraId);
  }, [nodeId, manager]);

  // Fullscreen
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Debug mode
  // `debugEnabled` is the raw app-wide flag; it's persisted, so it can outlive the role
  // that set it (role change, shared browser). `debugActive` is the one to render from.
  const [debugEnabled, setDebugEnabled] = useState(isDebugMode());
  const debugActive = canDebug && debugEnabled;

  /**
   * Network latency overlay — the same option the Live Wall offers, for the same reason: a cloud
   * viewer's picture crosses the node tunnel, and RTT is what separates "the stream is late" from
   * "the camera is stuck". Not persisted (unlike debug): it answers a question you ask while
   * watching, not a mode you work in.
   *
   * One figure per NODE, and this page is scoped to one, so every card shows the same number —
   * which is honest: it is the connection that is slow, not a camera.
   */
  const [showPing, setShowPing] = useState(false);
  const pingData = usePeerLatency(
    useCallback(() => {
      const mgr = manager();
      return mgr && nodeId ? [{ key: nodeId, pc: mgr.getNodePeerConnection(nodeId) }] : [];
    }, [manager, nodeId]),
    showPing,
  );
  const nodePing: PeerLatency | null = (nodeId ? pingData.get(nodeId) : null) || null;
  const [, setStatsTick] = useState(0);


  useEffect(() => {
    const unsub1 = subscribeDebugMode((enabled) => setDebugEnabled(enabled));
    const unsub2 = subscribeStats(() => setStatsTick(t => t + 1));
    return () => { unsub1(); unsub2(); };
  }, []);

  const getSignalingForMonitor = useCallback(() => {
    if (!nodeId) return null;
    return manager()?.getNodeSignalingInfo(nodeId) ?? null;
  }, [nodeId, manager]);

  // Debug stats: attach the cloud monitor to the shared node PC when debug is on
  // so the DebugOverlay keeps showing WebRTC stats. The packet-monitor round-trip
  // works here too — NodeConnectionManager routes packet_monitor_* centrally, so
  // a page no longer has to own the signaling socket to receive results.
  useEffect(() => {
    if (!debugActive || !nodeId) return;
    let stopped = false;
    const tryAttach = () => {
      if (stopped) return;
      const pc = manager()?.getNodePeerConnection(nodeId);
      if (pc) attachCloudMonitor(pc);
      else setTimeout(tryAttach, 1000);
    };
    tryAttach();
    return () => { stopped = true; stopCloudMonitor(); };
  }, [debugActive, nodeId, manager]);

  useEffect(() => {
    function onFsChange() { setIsFullscreen(!!(document.fullscreenElement || (document as any).webkitFullscreenElement)); }
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = gridContainerRef.current;
    if (!el) return;
    const fsEl = document.fullscreenElement || (document as any).webkitFullscreenElement;
    if (!fsEl) {
      const reqFs = el.requestFullscreen?.bind(el) || (el as any).webkitRequestFullscreen?.bind(el);
      if (reqFs) reqFs().catch(() => { });
    } else {
      const exitFs = document.exitFullscreen?.bind(document) || (document as any).webkitExitFullscreen?.bind(document);
      if (exitFs) exitFs().catch(() => { });
    }
  }, []);

  // Escape to close enlarged
  useEffect(() => {
    if (!selectedCameraForEnlarged) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setSelectedCameraForEnlarged(null);
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedCameraForEnlarged]);

  // Guards the group-filter effect below against firing for a fetch the load effect has
  // already made — on first mount, and on the site change that clears the filter.
  const filterFetchRef = useRef(0);

  // Fetch data
  useEffect(() => {
    if (!selectedNode) {
      setCameras([]);
      setGroups([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    /**
     * The group filter belongs to the site it was picked on. Carried into another site it is a
     * list of group ids that site has never heard of, and the node answers a filter it can't
     * match with nothing — so the new site's camera list would open EMPTY, which reads as a
     * site with no cameras rather than as a filter left behind.
     *
     * Cleared here rather than in an effect of its own precisely because the request below
     * would otherwise be the one that carries it over. Only when there IS one, so that a
     * genuine filter change isn't swallowed by the skip below.
     */
    if (selectedGroups.length) {
      setSelectedGroups([]);
      // Re-arm the filter effect's first-run skip: clearing the filter changes
      // `groupFilterParam`, which would otherwise fire a second camera fetch for this one
      // switch — the request right here is already the unfiltered one.
      filterFetchRef.current = 0;
    }
    setPage(1);
    // Users without groups.view still get the groups that contain their
    // accessible cameras (via /groups/for-filter) so they can filter.
    const groupFetch = hasPermission('groups.view')
      ? getNodeGroups(selectedNode.nodeId)
      : getNodeGroupsForFilter(selectedNode.nodeId);
    Promise.all([
      getNodeCameras(selectedNode.nodeId, { groupIds: [] }).catch(() => []),
      groupFetch.catch(() => []),
      getNodeModelAssignments(selectedNode.nodeId).catch(() => null),
    ]).then(([camData, grpData, assignData]) => {
      const cams = Array.isArray(camData) ? camData : camData?.data || camData?.cameras || [];
      setCameras(cams);
      const grps = Array.isArray(grpData) ? grpData : grpData?.data || [];
      setGroups(grps);
      // Build camera → model names map, and KEEP THE RAW PAYLOAD: the map is display-only
      // (names, flattened, with no memory of which model came from the camera and which from
      // a group), and the row's "AI Models…" dialog needs exactly that distinction.
      if (assignData) {
        const d = assignData.models ? assignData : (assignData.data || assignData);
        setAssignData(d);
        setCameraModelMap(buildCameraModelMap(d, cams));
      }
    }).finally(() => setLoading(false));
  }, [selectedNode?.nodeId]);

  // Refresh cameras helper
  async function refreshCameras() {
    if (!selectedNode) return;
    try {
      const camData = await getNodeCameras(selectedNode.nodeId);
      setCameras(Array.isArray(camData) ? camData : camData?.data || []);
    } catch { /* ignore */ }
  }

  /**
   * The group filter is a SERVER filter, so changing it refetches.
   *
   * It used to be matched in the browser against the already-loaded list, which meant the
   * filter made no request at all — and matched only a camera's own groups, so picking a
   * parent group silently dropped every camera that lives in one of its sub-groups. The node
   * resolves the subtree; this just asks it again. Cameras only — groups and model assignments
   * don't change with the filter, and refetching them would flicker the whole page.
   */
  useEffect(() => {
    if (!selectedNode) return;
    // Skips the first run: the load effect above already fetched with this filter.
    if (filterFetchRef.current === 0) { filterFetchRef.current = 1; return; }
    let cancelled = false;
    const ids = groupFilterParam ? groupFilterParam.split(',') : [];
    getNodeCameras(selectedNode.nodeId, { groupIds: ids })
      .then((camData: any) => {
        if (cancelled) return;
        setCameras(Array.isArray(camData) ? camData : camData?.data || camData?.cameras || []);
      })
      .catch(() => { if (!cancelled) setCameras([]); });
    return () => { cancelled = true; };
    // selectedNode is deliberately absent: a node change is the load effect's job, and
    // including it here would fire two camera fetches for one switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupFilterParam]);

  /* ── Row-level assignment: AI models, and group membership ───────────────────────────
     Both dialogs open from the list's row menu and write through the very endpoints the
     Model Assignment and Group Camera Map pages use, so an edit made here and an edit made
     there are the same edit. ─────────────────────────────────────────────────────────── */
  const [modelsFor, setModelsFor] = useState<any | null>(null);
  const [groupsFor, setGroupsFor] = useState<any | null>(null);
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  /**
   * Everything on this page that names a CAMERA is dropped when the site changes: a bulk
   * selection, an open edit/assign dialog or an enlarged tile all hold ids from the site the
   * operator has just left, and acting on them would send another site's camera ids to this
   * node. The list itself refetches above; this is only the pointers into it.
   */
  const resetForNodeRef = useRef<string | null>(null);
  useEffect(() => {
    const nid = selectedNode?.nodeId ?? null;
    if (resetForNodeRef.current === nid) return;
    const first = resetForNodeRef.current === null;
    resetForNodeRef.current = nid;
    if (first) return;
    setSelectedCameraIds(new Set());
    setSelectedCameraForEnlarged(null);
    setEditingCamera(null);
    setModelsFor(null);
    setGroupsFor(null);
    setShowBulkDeleteConfirm(false);
    setShowBulkRecording(false);
    setError('');
  }, [selectedNode?.nodeId]);

  /** camera id → group ids it belongs to. */
  const cameraGroupIds = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const m of (assignData?.camera_group_memberships || [])) {
      if (!map[m.camera_id]) map[m.camera_id] = [];
      map[m.camera_id].push(m.group_id);
    }
    return map;
  }, [assignData]);

  /** group id → the model ids assigned to that group. */
  const groupModelIds = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const a of (assignData?.group_assignments || [])) {
      if (!map[a.group_id]) map[a.group_id] = [];
      map[a.group_id].push(a.model_id);
    }
    return map;
  }, [assignData]);

  /** The group list as the dialog wants it: depth for the indent, ancestors for the subtitle. */
  const assignGroupOptions = useMemo(() => {
    const out: any[] = [];
    const walk = (nodes: any[], depth: number, trail: string[]) => {
      for (const g of nodes || []) {
        out.push({ id: g.id, name: g.name, depth, path: trail.join(' › ') });
        if (g.children?.length) walk(g.children, depth + 1, [...trail, g.name]);
      }
    };
    walk(Array.isArray(groups) ? groups : [], 0, []);
    return out;
  }, [groups]);

  const reloadAssignments = useCallback(async (cams?: any[]) => {
    if (!selectedNode) return;
    const res: any = await getNodeModelAssignments(selectedNode.nodeId).catch(() => null);
    if (!res) return;
    const d = res.models ? res : (res.data || res);
    setAssignData(d);
    setCameraModelMap(buildCameraModelMap(d, cams || cameras));
  }, [selectedNode?.nodeId, cameras]);

  /**
   * Save this camera's DIRECT model assignments.
   *
   * `managed_camera_ids` is the whole safety of doing this from a row: the node's bulk
   * endpoint clears and rewrites only the scopes it is handed, so naming this one camera —
   * and no groups at all — leaves every other camera's and every group's assignments alone.
   */
  const saveCameraModels = async (modelIds: string[]) => {
    if (!modelsFor || !selectedNode) return;
    const camId = modelsFor.id || modelsFor.camera_id;
    setAssignBusy(true);
    setAssignError(null);
    try {
      await bulkSaveNodeModelAssignments(selectedNode.nodeId, {
        camera_assignments: modelIds.map((model_id) => ({ camera_id: camId, model_id, is_active: true })),
        managed_camera_ids: [camId],
        group_assignments: [],
        managed_group_ids: [],
      });
      await reloadAssignments();
      setModelsFor(null);
    } catch (err: any) {
      setAssignError(err?.response?.data?.message || err?.message || 'Failed to save model assignments');
    } finally {
      setAssignBusy(false);
    }
  };

  /**
   * Join and leave groups. The node has an add and a remove endpoint and no "set memberships"
   * one, so the dialog hands over the diff and this walks it — sequentially, since each hop
   * is a tunnel round trip and firing them together only makes a partial failure harder to
   * report.
   */
  const saveCameraGroups = async ({ add, remove }: { add: string[]; remove: string[] }) => {
    if (!groupsFor || !selectedNode) return;
    const camId = groupsFor.id || groupsFor.camera_id;
    setAssignBusy(true);
    setAssignError(null);
    try {
      for (const gid of add) await addCamerasToGroup(selectedNode.nodeId, gid, [camId]);
      for (const gid of remove) await removeCameraFromGroup(selectedNode.nodeId, gid, camId);
      // Membership decides which models a camera INHERITS and which group filter it appears
      // under, so both the camera rows and the assignment payload are now stale.
      const camData: any = await getNodeCameras(selectedNode.nodeId).catch(() => null);
      const cams = camData ? (Array.isArray(camData) ? camData : camData?.data || []) : cameras;
      if (camData) setCameras(cams);
      await reloadAssignments(cams);
      setGroupsFor(null);
    } catch (err: any) {
      setAssignError(err?.response?.data?.message || err?.message || 'Failed to update groups');
    } finally {
      setAssignBusy(false);
    }
  };

  async function handleDeleteCamera(cameraId: string) {
    if (!selectedNode) return;
    try {
      await deleteCamera(selectedNode.nodeId, cameraId);
      const camData = await getNodeCameras(selectedNode.nodeId);
      setCameras(Array.isArray(camData) ? camData : camData?.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to delete camera');
    }
  }

  function toggleCameraSelection(cameraId: string) {
    setSelectedCameraIds(prev => {
      const next = new Set(prev);
      next.has(cameraId) ? next.delete(cameraId) : next.add(cameraId);
      return next;
    });
  }

  // The list table paginates itself and hands over the ids of the page it is showing.
  function toggleSelectAll(pageIds: string[]) {
    if (pageIds.length > 0 && pageIds.every((id) => selectedCameraIds.has(id))) {
      setSelectedCameraIds(new Set());
    } else {
      setSelectedCameraIds(new Set(pageIds));
    }
  }

  async function openBulkRecording() {
    if (!selectedNode) return;
    setShowBulkRecording(true);
    try {
      // Fetch the node's own modes/event types/limits so the form can never offer a value
      // its validator rejects. Non-fatal: the modal falls back to its built-in lists.
      const res: any = await getRecordingConfigs(selectedNode.nodeId);
      setRecordingMeta({ defaults: res?.defaults || {}, options: res?.options || {} });
    } catch {
      /* keep the modal open with built-in defaults */
    }
  }

  async function handleBulkRecordingApply(patch: any) {
    if (!selectedNode) return;
    await bulkUpdateRecordingConfig(selectedNode.nodeId, [...selectedCameraIds], patch);
    setSelectedCameraIds(new Set());
    const camData = await getNodeCameras(selectedNode.nodeId);
    setCameras(Array.isArray(camData) ? camData : camData?.data || []);
  }

  async function handleBulkDelete() {
    if (!selectedNode || selectedCameraIds.size === 0) return;
    setBulkDeleting(true);
    try {
      await Promise.all([...selectedCameraIds].map(id => deleteCamera(selectedNode.nodeId, id)));
      const camData = await getNodeCameras(selectedNode.nodeId);
      setCameras(Array.isArray(camData) ? camData : camData?.data || []);
      setSelectedCameraIds(new Set());
    } catch (err: any) {
      setError(err.message || 'Failed to delete some cameras');
    } finally {
      setBulkDeleting(false);
      setShowBulkDeleteConfirm(false);
    }
  }

  // Filter cameras
  const filteredCameras = cameras.filter(cam => {
    const matchSearch = !search || cam.name?.toLowerCase().includes(search.toLowerCase());
    // No group check here: the node filtered the list, sub-groups included. Re-checking the
    // camera's own groups in the browser would throw away exactly those sub-group cameras.
    return matchSearch;
  });

  // Pagination
  const pageSize = viewMode === 'grid' ? PAGE_SIZE_GRID : PAGE_SIZE_LIST;
  const totalPages = Math.ceil(filteredCameras.length / pageSize);
  const currentPage = Math.min(page, totalPages || 1);
  const paginatedCameras = filteredCameras.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, selectedGroupIds, viewMode]);

  // Reconcile subscriptions on the shared connection with the visible grid page:
  // subscribe cameras that appear, unsubscribe those that leave (so the SFU stops
  // pushing off-screen tracks). Only stream in grid view. The shared WS+PC stays
  // warm across pagination/search/navigation — no per-change ICE/TURN rebuild.
  const subscribedIdsRef = useRef<Set<string>>(new Set());
  const visibleIdsKey = paginatedCameras.map((c: any) => c.id || c.camera_id).join(',');
  useEffect(() => {
    if (!nodeId) return;
    const visible = (viewMode === 'grid' && visibleIdsKey) ? visibleIdsKey.split(',') : [];
    const visibleSet = new Set(visible);
    for (const id of subscribedIdsRef.current) {
      if (!visibleSet.has(id)) unsubscribe(nodeId, id);
    }
    for (const id of visible) {
      if (!subscribedIdsRef.current.has(id)) subscribe(nodeId, nodeName, id);
    }
    subscribedIdsRef.current = visibleSet;
  }, [nodeId, nodeName, visibleIdsKey, viewMode, subscribe, unsubscribe]);

  // Release all of this page's subscriptions on unmount (shared connection stays).
  useEffect(() => {
    return () => {
      if (nodeId) for (const id of subscribedIdsRef.current) unsubscribe(nodeId, id);
      subscribedIdsRef.current = new Set();
    };
  }, [nodeId, unsubscribe]);

  const gridCols = GRID_LAYOUTS.find(l => l.key === gridLayout)?.cols || 3;

  if (!selectedNode) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <VideoCameraIcon className="h-12 w-12 mb-4 opacity-40" />
        <p className="text-[15px] font-medium">No site selected</p>
        <p className="mt-1 text-[13px]">Select a site from the title bar to view cameras.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h1 className="text-xl font-semibold text-foreground">{manageMode ? 'Edit Cameras' : 'Camera List'}</h1>

      {/* Filter Bar */}
      <div className="rounded-lg border border-border bg-card shadow-card">
        <div className="flex flex-wrap items-center gap-2 p-2 sm:gap-3 sm:p-3">
          {/* Search */}
          <div className="relative w-full sm:w-64 order-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search cameras…"
              className="w-full rounded-lg border border-border bg-input py-2 pl-10 pr-4 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            />
          </div>

          {/* Group filter */}
          <div className="relative order-3 sm:order-2">
            <button
              onClick={() => setShowGroupDropdown(!showGroupDropdown)}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-[13px] font-medium transition-colors whitespace-nowrap ${selectedGroups.length
                ? 'border-brand bg-brand/5 text-brand'
                : 'border-border bg-card text-muted-foreground hover:bg-muted'
                }`}
            >
              <FunnelIcon className="h-4 w-4" />
              {/* One group reads better by name; past that a count is what fits in a toolbar. */}
              <span className="hidden max-w-[14rem] truncate sm:inline">
                {selectedGroups.length === 0
                  ? 'Filter by Group'
                  : selectedGroups.length === 1
                    ? `Group: ${selectedGroups[0].name}`
                    : `${selectedGroups.length} groups`}
              </span>
              {selectedGroups.length > 0 && <span className="sm:hidden">{selectedGroups.length}</span>}
              <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${showGroupDropdown ? 'rotate-180' : ''}`} />
            </button>
            {showGroupDropdown && (
              <div className="absolute top-full left-0 mt-1 w-64 rounded-xl border border-border bg-card shadow-lg z-40 p-2 max-h-72 overflow-y-auto">
                {/* Names the behaviour: nothing else in this dropdown says a second pick adds
                    to the first rather than replacing it. */}
                <div className="flex items-center gap-2 px-2 pb-1.5">
                  <span className="text-[11px] text-muted-foreground">Pick one or more</span>
                  {selectedGroups.length > 0 && (
                    <button
                      onClick={() => setSelectedGroups([])}
                      className="ml-auto text-[11px] font-medium text-brand hover:underline"
                    >
                      Clear ({selectedGroups.length})
                    </button>
                  )}
                </div>
                {/* What's filtering right now, each chip removable — a deep tree can hide the
                    chosen rows behind a collapsed parent. */}
                {selectedGroups.length > 0 && (
                  <div className="mb-1.5 flex flex-wrap gap-1.5 border-b border-border px-1 pb-2">
                    {selectedGroups.map((g: any) => (
                      <span key={g.id} className="inline-flex max-w-full items-center gap-1 rounded-full bg-brand/10 px-2 py-0.5 text-[11px] font-medium text-brand">
                        <span className="truncate">{g.name}</span>
                        <button onClick={() => toggleGroup(g)} aria-label={`Remove ${g.name} from the filter`} className="flex-shrink-0 rounded hover:bg-brand/20">
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {/* Stays open on a pick: choosing several groups one click at a time is the
                    point, and closing after the first would make the second cost two clicks. */}
                {renderGroupTree(buildGroupTree(groups), selectedGroupIds, toggleGroup)}
                {groups.length === 0 && <p className="px-2 py-3 text-[12px] text-muted-foreground text-center">No groups</p>}
              </div>
            )}
          </div>

          {/* Clear */}
          {/* Toolbar actions are icon-only, with the label moved into a hover tooltip —
              same treatment as the Multi-Site toolbar, so a long filter bar stays legible
              on narrow windows. */}
          {(search || selectedGroups.length > 0) && (
            <Tooltip label="Clear search and group filter" side="bottom" className="order-4 sm:order-3">
              <button
                onClick={() => { setSearch(''); setSelectedGroups([]); }}
                aria-label="Clear filters"
                className="flex items-center rounded-lg border border-border p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          )}

          <div className="flex-1 order-2 sm:order-4 hidden sm:block" />

          {/* View mode toggles */}
          <div className="flex rounded-lg border border-border bg-card p-0.5 order-5">
            {/* Grid button with layout dropdown — live wall belongs to Live View only */}
            <div className={`relative ${manageMode ? 'hidden' : ''}`}>
              <Tooltip label={viewMode === 'grid' ? 'Grid view — click again to choose a layout' : 'Grid view'} side="bottom">
                <button
                  onClick={() => {
                    if (viewMode === 'grid') {
                      setShowGridDropdown(!showGridDropdown);
                    } else {
                      setViewMode('grid');
                    }
                  }}
                  aria-label="Grid view"
                  className={`flex items-center gap-1 rounded-md px-2 py-1.5 text-[11px] font-medium transition-colors ${viewMode === 'grid' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'
                    }`}
                >
                  <Squares2X2Icon className="h-4 w-4" />
                  {viewMode === 'grid' && (
                    <ChevronDownIcon className={`h-3 w-3 transition-transform ${showGridDropdown ? 'rotate-180' : ''}`} />
                  )}
                </button>
              </Tooltip>
              {viewMode === 'grid' && showGridDropdown && (
                <div className="absolute top-full right-0 mt-1 rounded-lg border border-border bg-card shadow-xl z-50 min-w-max overflow-hidden">
                  {GRID_LAYOUTS.map((layout, index) => (
                    <button
                      key={layout.key}
                      onClick={() => { setGridLayout(layout.key); setShowGridDropdown(false); }}
                      className={`block w-full px-4 py-3 text-left text-[14px] font-medium transition-colors ${gridLayout === layout.key ? 'bg-muted text-foreground' : 'text-foreground hover:bg-muted'
                        } ${index < GRID_LAYOUTS.length - 1 ? 'border-b border-border' : ''}`}
                    >
                      {layout.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* List button */}
            <Tooltip label="List view" side="bottom">
              <button
                onClick={() => setViewMode('list')}
                aria-label="List view"
                className={`flex items-center rounded-md px-2 py-1.5 text-[11px] font-medium transition-colors ${viewMode === 'list' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
              >
                <ListBulletIcon className="h-4 w-4" />
              </button>
            </Tooltip>
            {/* Tree button */}
            <Tooltip label="Group tree view" side="bottom">
              <button
                onClick={() => setViewMode('tree')}
                aria-label="Group tree view"
                className={`flex items-center rounded-md px-2 py-1.5 text-[11px] font-medium transition-colors ${viewMode === 'tree' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
              >
                <FolderIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          </div>

          {/* Debug toggle */}

          {canDebug && (
            <Tooltip label={debugEnabled ? 'Debug stats ON — click to hide WebRTC stats' : 'Show WebRTC debug stats'} side="bottom" className="order-7">
              <button
                onClick={() => setDebugMode(!debugEnabled)}
                aria-label="Toggle debug stats"
                className={`flex items-center rounded-lg border p-2 transition-colors ${debugEnabled
                  ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
                  : 'border-border bg-card text-muted-foreground hover:bg-muted'
                  }`}
              >
                <BugAntIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          )}

          {/* Network latency toggle — same option, same green, as the Live Wall's. Grid view only:
              the readout is drawn over a live frame, and the list and tree views have none. */}
          {viewMode === 'grid' && (
            <Tooltip label={showPing ? 'Hide network latency' : 'Show network latency'} side="bottom" className="order-7">
              <button
                onClick={() => setShowPing((v) => !v)}
                aria-label="Toggle network latency"
                className={`flex items-center rounded-lg border p-2 transition-colors ${showPing
                  ? 'bg-green-500/15 text-green-400 border-green-500/30'
                  : 'border-border bg-card text-muted-foreground hover:bg-muted'
                  }`}
              >
                <Activity className="h-4 w-4" />
              </button>
            </Tooltip>
          )}

          {/* Bulk upload — beside Add, because "I have forty of these" is the same errand.
              Imports land on the SELECTED site; the page says which one. */}
          {canAdd && (
            <Tooltip label="Bulk upload from CSV or Excel" side="bottom" className="order-8">
              <Link
                to="/cameras/import"
                aria-label="Bulk upload cameras from a spreadsheet"
                className="flex items-center rounded-lg border border-border p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <ArrowUpTrayIcon className="h-4 w-4" />
              </Link>
            </Tooltip>
          )}

          {/* Add camera button */}

          {canAdd && (
            <Tooltip label="Add camera" side="bottom" className="order-8">
              <button
                onClick={() => setShowAdd(true)}
                aria-label="Add camera"
                className="flex items-center rounded-lg bg-brand p-2 text-white hover:bg-brand-dark transition-colors"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          )}

          {/* Fullscreen toggle */}
          {viewMode === 'grid' && (
            <Tooltip label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} side="bottom" className="order-9">
              <button
                onClick={toggleFullscreen}
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                className="flex items-center rounded-lg border border-border p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                {isFullscreen ? <ArrowsPointingInIcon className="h-4 w-4" /> : <ArrowsPointingOutIcon className="h-4 w-4" />}
              </button>
            </Tooltip>
          )}

        </div>
      </div>

      {/* Bulk select bar */}
      {selectedCameraIds.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2 rounded-lg border border-destructive/30 bg-destructive/5">
          <span className="text-[13px] text-foreground font-medium">{selectedCameraIds.size} camera{selectedCameraIds.size !== 1 ? 's' : ''} selected</span>
          {canConfigRecording && (
            <button onClick={openBulkRecording}
              className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 text-[13px] font-semibold text-foreground hover:bg-muted transition-colors">
              <VideoCameraIcon className="h-4 w-4" />
              Recording Settings
            </button>
          )}
          {canDelete && (
            <button onClick={() => setShowBulkDeleteConfirm(true)}
              className="flex items-center gap-2 rounded-lg bg-destructive px-3 py-1.5 text-[13px] font-semibold text-white hover:bg-destructive/80 transition-colors">
              <TrashIcon className="h-4 w-4" />
              Delete Selected
            </button>
          )}
          <button onClick={() => setSelectedCameraIds(new Set())} className="text-[13px] text-muted-foreground hover:text-foreground">Clear</button>
        </div>
      )}

      {/* Bulk recording-mode editor */}
      <BulkRecordingModeModal
        isOpen={showBulkRecording}
        onClose={() => setShowBulkRecording(false)}
        cameraIds={[...selectedCameraIds]}
        options={recordingMeta.options}
        defaults={recordingMeta.defaults}
        onApply={handleBulkRecordingApply}
        // Matches CameraDetailPage: both trigger sources are live.
        eventModeAvailable
        motionModeAvailable
      />

      {/* ── Row-level assignment ── */}
      {modelsFor && (
        <CameraModelsModal
          cameraName={modelsFor.name || modelsFor.id || modelsFor.camera_id}
          models={assignData?.models || []}
          assigned={(assignData?.camera_assignments || [])
            .filter((a: any) => a.camera_id === (modelsFor.id || modelsFor.camera_id))
            .map((a: any) => a.model_id)}
          /* What this camera already gets from its groups, and from WHICH group — the dialog
             locks those rows and names the group, because only editing that group can take
             one away. */
          inheritedFrom={(cameraGroupIds[modelsFor.id || modelsFor.camera_id] || []).reduce((acc: Record<string, string[]>, gid: string) => {
            const gname = assignGroupOptions.find((g: any) => g.id === gid)?.name || 'a group';
            for (const mid of (groupModelIds[gid] || [])) {
              if (!acc[mid]) acc[mid] = [];
              if (!acc[mid].includes(gname)) acc[mid].push(gname);
            }
            return acc;
          }, {})}
          busy={assignBusy}
          error={assignError}
          onClose={() => setModelsFor(null)}
          onSave={saveCameraModels}
        />
      )}

      {groupsFor && (
        <CameraGroupsModal
          cameraName={groupsFor.name || groupsFor.id || groupsFor.camera_id}
          groups={assignGroupOptions}
          member={cameraGroupIds[groupsFor.id || groupsFor.camera_id] || []}
          busy={assignBusy}
          error={assignError}
          onClose={() => setGroupsFor(null)}
          onSave={saveCameraGroups}
        />
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl w-full max-w-md border border-border shadow-2xl">
            <div className="px-6 py-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
                  <TrashIcon className="h-5 w-5 text-destructive" />
                </div>
                <h2 className="text-lg font-semibold text-foreground">Delete Cameras</h2>
              </div>
              <p className="text-[14px] text-muted-foreground">
                Are you sure you want to delete <span className="font-semibold text-foreground">{selectedCameraIds.size}</span> camera{selectedCameraIds.size !== 1 ? 's' : ''}? This action cannot be undone.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
              <button onClick={() => setShowBulkDeleteConfirm(false)} disabled={bulkDeleting}
                className="px-4 py-2 rounded-lg text-[13px] font-medium text-muted-foreground hover:bg-muted transition-colors">
                Cancel
              </button>
              <button onClick={handleBulkDelete} disabled={bulkDeleting}
                className="px-4 py-2 rounded-lg text-[13px] font-semibold bg-destructive text-white hover:bg-destructive/80 transition-colors disabled:opacity-50 flex items-center gap-2">
                {bulkDeleting && <div className="animate-spin rounded-full h-3.5 w-3.5 border-t-2 border-white" />}
                {bulkDeleting ? 'Deleting…' : `Delete ${selectedCameraIds.size} Camera${selectedCameraIds.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Camera Modal */}
      <CameraFormModal
        isOpen={showAdd}
        onClose={() => setShowAdd(false)}
        onSuccess={() => { setShowAdd(false); refreshCameras(); }}
        nodeId={selectedNode?.nodeId || ''}
        groups={flatGroups}
      />

      {/* Edit Camera Modal */}
      <CameraFormModal
        isOpen={!!editingCamera}
        onClose={() => setEditingCamera(null)}
        onSuccess={() => { setEditingCamera(null); refreshCameras(); }}
        camera={editingCamera}
        nodeId={selectedNode?.nodeId || ''}
        groups={flatGroups}
      />

      {/* Error */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/30 text-destructive px-4 py-2.5 rounded-lg text-[13px]">
          {error}
          <button onClick={() => setError('')} className="ml-2 underline">dismiss</button>
        </div>
      )}

      {/* Per-camera connection state is shown on each tile (LiveStreamPlayer);
          the shared connection has no single page-level status to surface here. */}

      {/* Content */}
      <div>
      {loading && cameras.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-brand" />
        </div>
      ) : viewMode === 'grid' ? (
        /* ──── Grid — Live Streaming Wall ──── */
        <>
          {paginatedCameras.length > 0 ? (
            selectedCameraForEnlarged ? (
              <div ref={gridContainerRef} className={`overflow-hidden relative ${isFullscreen ? 'bg-black' : 'h-[60vh] sm:h-[calc(100vh-200px)]'}`}
                style={isFullscreen ? { width: '100vw', height: '100vh' } : undefined}
              >
                <div className="h-full">
                  <div className="grid h-full" style={{ gridTemplateColumns: '1fr' }}>
                    {(() => { const eid = selectedCameraForEnlarged.id || selectedCameraForEnlarged.camera_id; return (
                    <LiveVideoCard camera={selectedCameraForEnlarged} stream={(nodeId ? allStreams.get(`${nodeId}:${eid}`) : null) || null} showDebug={debugActive} getSignaling={getSignalingForMonitor} connectionState={(nodeId ? states.get(`${nodeId}:${eid}`) : undefined) || 'pending'} onStalled={() => handleCameraStall(eid)} onPlaying={() => handleCameraPlaying(eid)} onRetry={() => handleCameraRetry(eid)} isFullscreen={isFullscreen} canConfig={canConfig} modelNames={cameraModelMap[eid] || []} pingInfo={nodePing} />
                    ); })()}
                  </div>
                </div>
                {/* Floating controls — top bar */}
                <div className="absolute top-3 left-3 right-3 flex items-center justify-between z-20 pointer-events-none">
                  <button onClick={() => setSelectedCameraForEnlarged(null)} className="pointer-events-auto flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-medium text-white bg-black/60 hover:bg-black/80 backdrop-blur-sm transition-colors" title="Back to grid (Esc)">
                    <XMarkIcon className="h-4 w-4" />
                    <span>Back</span>
                  </button>
                  <div className="pointer-events-auto flex items-center gap-2">
                    <span className="text-[12px] font-medium text-white/80 bg-black/40 backdrop-blur-sm rounded-lg px-2.5 py-1">{selectedCameraForEnlarged.name}</span>
                    <button onClick={toggleFullscreen} className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-medium text-white bg-black/60 hover:bg-black/80 backdrop-blur-sm transition-colors">
                      {isFullscreen ? <ArrowsPointingInIcon className="h-4 w-4" /> : <ArrowsPointingOutIcon className="h-4 w-4" />}
                      <span>{isFullscreen ? 'Exit' : 'Fullscreen'}</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div ref={gridContainerRef} className={`relative ${isFullscreen ? 'bg-black flex flex-col' : ''}`}
                style={isFullscreen ? { width: '100vw', height: '100vh', padding: '8px' } : undefined}
              >
                {isFullscreen && (
                  <button
                    onClick={toggleFullscreen}
                    className="absolute top-3 right-3 z-20 rounded-lg p-2 text-white bg-black/60 hover:bg-black/80 transition-colors"
                  >
                    <ArrowsPointingInIcon className="h-5 w-5" />
                  </button>
                )}
                <div className={`camera-live-grid grid grid-cols-1 sm:grid-cols-2 md:[grid-template-columns:repeat(var(--grid-cols),minmax(0,1fr))] ${isFullscreen ? 'flex-1 gap-1' : 'gap-2'}`}
                  style={{ '--grid-cols': gridCols } as React.CSSProperties}
                >
                  {paginatedCameras.map((camera) => {
                    const camId = camera.id || camera.camera_id;
                    return (
                      <LiveVideoCard
                        key={camId}
                        camera={camera}
                        stream={(nodeId ? allStreams.get(`${nodeId}:${camId}`) : null) || null}
                        onEnlarge={() => setSelectedCameraForEnlarged(camera)}
                        // onEdit={() => setEditingCamera(camera)}
                        onEdit={canEdit ? () => setEditingCamera(camera) : undefined}
                        canConfig={canConfig}
                        showDebug={debugActive}
                        getSignaling={getSignalingForMonitor}
                        connectionState={(nodeId ? states.get(`${nodeId}:${camId}`) : undefined) || 'pending'}
                        onStalled={() => handleCameraStall(camId)}
                        onPlaying={() => handleCameraPlaying(camId)}
                        onRetry={() => handleCameraRetry(camId)}
                        isFullscreen={isFullscreen}
                        modelNames={cameraModelMap[camId] || []}
                        pingInfo={nodePing}
                      />
                    );
                  })}
                </div>
              </div>
            )
          ) : (
            <EmptyState groupNames={selectedGroups.map((g: any) => g.name)} onClearFilter={() => setSelectedGroups([])} />
          )}
        </>
      ) : viewMode === 'list' ? (
        /* ──── List (table) view ──── */
        filteredCameras.length > 0 ? (
          <CameraListTable
            cameras={filteredCameras}
            resetKey={`${search}|${groupFilterParam}`}
            loading={loading}
            selectedIds={canBulkSelect ? selectedCameraIds : undefined}
            onToggleSelect={canBulkSelect ? toggleCameraSelection : undefined}
            onToggleSelectAll={canBulkSelect ? toggleSelectAll : undefined}
            onEdit={canEdit ? (cam: any) => setEditingCamera(cam) : undefined}
            onDelete={canDelete ? handleDeleteCamera : undefined}
            onView={(cam: any) => navigate(`/nodes/${selectedNode?.nodeId}/cameras/${cam.id || cam.camera_id}`)}
            onConfig={canConfig ? (cam: any) => navigate(`/nodes/${selectedNode?.nodeId}/cameras/${cam.id || cam.camera_id}/config`) : undefined}
            onHeatmap={(cam: any) => navigate(`/nodes/${selectedNode?.nodeId}/cameras/${cam.id || cam.camera_id}/heatmap?name=${encodeURIComponent(cam.name || 'Camera')}`)}
            onModelReport={hasPermission('ai_events.view')
              ? (cam: any, surface: string) => navigate(`/reports/${surface}?camera_id=${encodeURIComponent(cam.id || cam.camera_id)}`)
              : undefined}
            onAssignModels={canAssignModels ? (cam: any) => { setAssignError(null); setModelsFor(cam); } : undefined}
            onAssignGroups={canAssignGroups ? (cam: any) => { setAssignError(null); setGroupsFor(cam); } : undefined}
            cameraModelMap={cameraModelMap}
          />
        ) : <EmptyState groupNames={selectedGroups.map((g: any) => g.name)} onClearFilter={() => setSelectedGroups([])} />
      ) : (
        /* ──── Tree view ──── */
        <CameraTree
          groups={groups}
          cameras={paginatedCameras}
          onCameraClick={(cam: any) => navigate(`/nodes/${selectedNode?.nodeId}/cameras/${cam.id || cam.camera_id}`)}
        />
      )}

      {/* Same shared bar as the list table's own footer — one footer, never two. */}
      {viewMode !== 'list' && (
        <ListPager page={currentPage} pageSize={pageSize} total={filteredCameras.length} onPageChange={setPage} itemLabel="cameras" />
      )}
      </div>
    </div>
  );
}

/* ─── Sub-Components ─────────────────────────────────────────────── */

function LiveVideoCard({ camera, stream, onEnlarge, onEdit, canConfig, showDebug, getSignaling, connectionState, onStalled, onPlaying, onRetry, isFullscreen: isFs, modelNames = [], pingInfo = null }: { camera: any; stream: MediaStream | null; onEnlarge?: () => void; onEdit?: () => void; canConfig?: boolean; showDebug?: boolean; getSignaling?: () => { ws: WebSocket | null; pc: RTCPeerConnection | null; sessionId: string | null } | null; connectionState?: string; onStalled?: () => void; onPlaying?: () => void; onRetry?: () => void; isFullscreen?: boolean; modelNames?: string[]; pingInfo?: PeerLatency | null }) {
  const navigate = useNavigate();
  const { selectedNode } = useNode();
  const { hasPermission } = useAuth();
  const camId = camera.id || camera.camera_id;
  const stats = showDebug ? getCameraStats(camId) : null;
  // Packet-monitor state (subscription, run lifecycle, report) now lives inside
  // the shared PacketMonitorPanel rendered below, so this card no longer tracks
  // any of it.

  return (
    <div className={`group relative flex flex-col overflow-hidden transition ${isFs ? 'bg-black' : 'rounded-xl border border-border bg-card shadow-card hover:shadow-card-hover'}`}>
      {/* Title bar — hidden in fullscreen */}
      {!isFs && (
        <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-border/50 bg-muted/30 min-w-0">
          <Tooltip label={camera.name} side="top" disabled={!camera.name || camera.name.length <= 10} className="flex-shrink-0">
            <h3 className="text-[12px] font-semibold text-foreground">
              {camera.name && camera.name.length > 10 ? `${camera.name.slice(0, 10)}…` : camera.name}
            </h3>
          </Tooltip>
          {/* Assigned models — same chips as the node's own camera cards. Only the
              destination is ours: the cloud's routes are node-scoped. */}
          <CameraModelTags
            names={modelNames}
            hrefFor={(name: string, surface: string) =>
              surface === 'heatmap'
                ? (hasPermission('cameras.view') ? `/nodes/${selectedNode?.nodeId}/cameras/${camId}/heatmap?name=${encodeURIComponent(camera.name || 'Camera')}` : null)
                : (hasPermission('ai_events.view') ? `/reports/${surface}?camera_id=${encodeURIComponent(camId)}` : null)
            }
            onOpen={(href: string) => navigate(href)}
          />
        </div>
      )}

      {/* Video area */}
      <div className="relative aspect-video bg-black cursor-pointer" onClick={() => navigate(`/nodes/${selectedNode?.nodeId}/cameras/${camId}`)}>
        <LiveStreamPlayer stream={stream} connectionState={connectionState} onStalled={onStalled} onPlaying={onPlaying} onRetry={onRetry} />

        {/* Latency to the node — top right, the same corner and the same badge as the Live Wall's
            tiles, so the figure reads identically on both. The card's hover actions live
            bottom-right, so nothing is covered. Fullscreen drops it below the enlarged view's own
            name / fullscreen controls, which are pinned along the top. */}
        {pingInfo && <LatencyBadge latency={pingInfo} className={isFs ? 'top-14 right-3' : 'top-1.5 right-1.5'} />}
        {/* Hover action buttons — bottom right */}
        <div className="absolute bottom-2 right-2 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity z-10" onClick={e => e.stopPropagation()}>
          {onEdit && (
            <Tooltip label="Edit camera details" side="top">
              <button onClick={onEdit} className="rounded-lg bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors" aria-label="Edit camera details">
                <PencilIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          )}

          {canConfig && (
            <Tooltip label="ONVIF configuration — PTZ, imaging & stream profiles" side="top">
              <button onClick={() => navigate(`/nodes/${selectedNode?.nodeId}/cameras/${camId}/config`)} className="rounded-lg bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors" aria-label="ONVIF configuration">
                <Cog6ToothIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          )}
          {hasPermission('recordings.view') && (
            <Tooltip label="Recording playback — review this camera's past footage" side="top">
              <button onClick={() => navigate(`/recordings?camera=${camId}`)} className="rounded-lg bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors" aria-label="Recording playback">
                <FilmIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          )}
          <Tooltip label="Open this stream full screen in a new tab" side="top">
            <button onClick={() => window.open(`/stream/${selectedNode?.nodeId}/${camId}`, '_blank')} className="rounded-lg bg-black/60 p-1.5 text-white hover:bg-black/80 transition-colors" aria-label="Open stream in a new tab">
              <ArrowsPointingOutIcon className="h-4 w-4" />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Per-camera browser-side WebRTC stats (debug mode only) */}
      {showDebug && stats && (
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 px-3 py-1 text-[10px] font-mono text-muted-foreground border-t border-border/50 bg-muted/30">
          <span title="Frames per second"><span className="text-emerald-500">FPS</span> {stats.framesPerSecond != null ? Math.round(stats.framesPerSecond) : '—'}</span>
          <span title="Bitrate (kbps)"><span className="text-blue-400">BR</span> {stats.bitrateKbps != null ? stats.bitrateKbps : '—'}</span>
          <span title="Round-trip time (ms)"><span className="text-amber-400">RTT</span> {stats.rtt != null ? stats.rtt : '—'}ms</span>
          <span title="Jitter (ms)"><span className="text-purple-400">JTR</span> {stats.jitter != null ? stats.jitter : '—'}ms</span>
          <span title="Packet loss %"><span className="text-red-400">PL</span> {stats.packetLossRate != null ? (stats.packetLossRate * 100).toFixed(1) : '—'}%</span>
          <span title="Resolution"><span className="text-cyan-400">RES</span> {stats.frameWidth && stats.frameHeight ? `${stats.frameWidth}×${stats.frameHeight}` : '—'}</span>
          <span title="Frames dropped"><span className="text-orange-400">DRP</span> {stats.totalFramesDropped ?? '—'}</span>
          <span title="Packets received"><span className="text-teal-400">PKT</span> {stats.totalPacketsReceived != null ? stats.totalPacketsReceived.toLocaleString() : '—'}</span>
        </div>
      )}

      {/* End-to-end packet ledger, camera → browser. One shared
          implementation with the local-node dashboard — see
          @trinetra/shared components/diagnostics. */}
      {getSignaling && (
        <PacketMonitorPanel cameraId={camId} getSignaling={getSignaling} enabled={showDebug} />
      )}
    </div>
  );
}

function CameraCardView({ camera, onDelete }: { camera: any; onDelete: () => void }) {
  const navigate = useNavigate();
  const { selectedNode } = useNode();
  return (
    <div className="group rounded-xl border border-border bg-card p-4 shadow-card transition hover:shadow-card-hover">
      <div className="relative mb-3 flex h-36 items-center justify-center overflow-hidden rounded-lg bg-muted">
        <VideoCameraIcon className="h-12 w-12 text-muted-foreground" />
        <div className="absolute right-2 top-2">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${camera.status === 'active' || camera.status === 'streaming' || camera.status === 'recording'
            ? 'bg-emerald-500/10 text-emerald-500'
            : 'bg-muted text-muted-foreground'
            }`}>
            <span className={`h-1.5 w-1.5 rounded-full ${camera.status === 'active' || camera.status === 'streaming' || camera.status === 'recording'
              ? 'bg-emerald-400' : 'bg-muted-foreground'
              }`} />
            {camera.status || 'offline'}
          </span>
        </div>
      </div>
      <h3 className="truncate text-[13px] font-semibold text-foreground group-hover:text-brand">{camera.name}</h3>
      <div className="mt-2 space-y-1">
        <p className="truncate text-[10px] text-muted-foreground"><span className="font-medium">ID:</span> {camera.id || camera.camera_id}</p>
        <p className="break-all text-[10px] text-muted-foreground"><span className="font-medium">RTSP:</span> {camera.rtsp_url || 'N/A'}</p>
        <p className="break-all text-[10px] text-muted-foreground"><span className="font-medium">Sub RTSP:</span> {camera.sub_stream_rtsp_url || 'N/A'}</p>
      </div>
      <p className="mt-2 truncate text-[11px] text-muted-foreground">{camera.manufacturer || 'Unknown'} {camera.model || ''}</p>
      {camera.groups && camera.groups.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {camera.groups.slice(0, 3).map((g: any) => (
            <span key={g.id} className="inline-block max-w-[100px] truncate rounded-md bg-brand/10 px-1.5 py-0.5 text-[10px] font-medium text-brand">{g.name}</span>
          ))}
        </div>
      )}
      <div className="mt-3 pt-2 border-t border-border flex items-center gap-3">
        <button onClick={() => navigate(`/nodes/${selectedNode?.nodeId}/cameras/${camera.id || camera.camera_id}/config`)} className="text-[11px] text-brand hover:text-brand-dark transition-colors flex items-center gap-1"><Cog6ToothIcon className="h-3.5 w-3.5" />Config</button>
        <button onClick={onDelete} className="text-[11px] text-destructive hover:text-destructive/80 transition-colors">Delete</button>
      </div>
    </div>
  );
}

function derivePipelineStatus(cam: any) {
  return cam.live_pipeline ? 'online' : 'offline';
}

function statusBadgeClasses(status: string) {
  switch (status) {
    case 'online': return { badge: 'bg-emerald-500/10 text-emerald-500', dot: 'bg-emerald-400' };
    default: return { badge: 'bg-muted text-muted-foreground', dot: 'bg-muted-foreground' };
  }
}

// function CameraListView({ cameras, streams, cameraStateOverrides, connectionState, onDelete, onEnlarge, onEdit, selectedIds, onToggleSelect, onToggleSelectAll, cameraModelMap }: { cameras: any[]; streams: Map<string, MediaStream>; cameraStateOverrides: Map<string, string>; connectionState: string; onDelete: (id: string) => void; onEnlarge: (cam: any) => void; onEdit: (cam: any) => void; selectedIds: Set<string>; onToggleSelect: (id: string) => void; onToggleSelectAll: () => void; cameraModelMap: Record<string, string[]> }) {
//   const navigate = useNavigate();
//   const { selectedNode } = useNode();
//   const [openMenuId, setOpenMenuId] = useState<string | null>(null);
//   const allSelected = cameras.length > 0 && cameras.every(c => selectedIds.has(c.id || c.camera_id));
//   return (
//     <>
//       {/* Mobile card list */}
//       <div className="space-y-2 md:hidden">
//         {cameras.map((cam, idx) => (
//           <div key={cam.id || cam.camera_id} className="rounded-xl border border-border bg-card p-3 shadow-card">
//             <div className="flex items-center gap-3">
//               <input type="checkbox" checked={selectedIds.has(cam.id || cam.camera_id)} onChange={() => onToggleSelect(cam.id || cam.camera_id)} className="h-4 w-4 rounded border-border text-brand focus:ring-brand flex-shrink-0" />
//               <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted flex-shrink-0">
//                 <VideoCameraIcon className="h-4 w-4 text-muted-foreground" />
//               </div>
//               <div className="min-w-0 flex-1">
//                 <h4 className="truncate text-[13px] font-semibold text-foreground">{cam.name}</h4>
//                 <p className="truncate text-[11px] text-muted-foreground">{cam.ip_address || 'N/A'}</p>
//               </div>
//               {(() => {
//                 const ls = derivePipelineStatus(cam); const cls = statusBadgeClasses(ls); return (
//                   <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${cls.badge}`}>
//                     <span className={`h-1.5 w-1.5 rounded-full ${cls.dot}`} />
//                     {ls}
//                   </span>
//                 );
//               })()}
//             </div>
//             {cam.groups?.length > 0 && (
//               <div className="flex flex-wrap gap-1 mt-2">
//                 {cam.groups.slice(0, 3).map((g: any) => (
//                   <span key={g.id} className="inline-block rounded-md bg-brand/10 px-2 py-0.5 text-[10px] font-medium text-brand">{g.name}</span>
//                 ))}
//               </div>
//             )}
//             {(() => {
//               const camId = cam.id || cam.camera_id;
//               const modelNames = cameraModelMap[camId] || [];
//               if (modelNames.length === 0) return null;
//               return (
//                 <div className="flex flex-wrap gap-1 mt-2 items-center">
//                   {modelNames.slice(0, 3).map((name: string, i: number) => {
//                     const isHeatmap = name.toLowerCase().includes('heatmap');
//                     return isHeatmap ? (
//                       <button key={i} onClick={() => navigate(`/nodes/${selectedNode?.nodeId}/cameras/${camId}/heatmap?name=${encodeURIComponent(cam.name || 'Camera')}`)} title="View Heatmap" className="inline-block rounded-md bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 cursor-pointer hover:bg-purple-200 dark:hover:bg-purple-900/50 hover:ring-1 hover:ring-purple-400/50 transition-all">{name} ↗</button>
//                     ) : (
//                       <span key={i} className="inline-block rounded-md bg-sky-100 px-2 py-0.5 text-[10px] font-medium text-sky-700 dark:bg-sky-900/30 dark:text-sky-400">{name}</span>
//                     );
//                   })}
//                 </div>
//               );
//             })()}
//             <div className="flex items-center gap-3 mt-2 pt-2 border-t border-border">
//               <button onClick={() => navigate(`/nodes/${selectedNode?.nodeId}/cameras/${cam.id || cam.camera_id}`)} className="text-[12px] text-brand hover:text-brand-dark font-medium">Live</button>
//               <button onClick={() => onEdit(cam)} className="text-[12px] text-muted-foreground hover:text-foreground font-medium flex items-center gap-1"><PencilIcon className="h-3.5 w-3.5" />Edit</button>
//               <button onClick={() => navigate(`/nodes/${selectedNode?.nodeId}/cameras/${cam.id || cam.camera_id}/config`)} className="text-[12px] text-muted-foreground hover:text-foreground font-medium flex items-center gap-1"><Cog6ToothIcon className="h-3.5 w-3.5" />Config</button>
//               <button onClick={() => onDelete(cam.id || cam.camera_id)} className="text-[12px] text-destructive hover:text-destructive/80 font-medium">Delete</button>
//             </div>
//           </div>
//         ))}
//       </div>
//       {/* Desktop table */}
//       <div className="w-full overflow-x-auto rounded-lg border border-border bg-card shadow-card hidden md:block">
//         <table className="w-full text-[13px]">
//           <thead>
//             <tr className="border-b border-border bg-muted/50 sticky top-0">
//               <th className="px-4 py-3 text-left"><input type="checkbox" checked={allSelected} onChange={onToggleSelectAll} className="h-4 w-4 rounded border-border text-brand focus:ring-brand" /></th>
//               <th className="px-4 py-3 text-left font-semibold text-foreground">#</th>
//               <th className="px-4 py-3 text-left font-semibold text-foreground">Camera Name</th>
//               <th className="px-4 py-3 text-left font-semibold text-foreground">Camera ID</th>
//               <th className="px-4 py-3 text-left font-semibold text-foreground">Status</th>
//               <th className="px-4 py-3 text-left font-semibold text-foreground">IP Address</th>
//               <th className="px-4 py-3 text-left font-semibold text-foreground">RTSP URL</th>
//               <th className="px-4 py-3 text-left font-semibold text-foreground">Sub Stream RTSP</th>
//               <th className="px-4 py-3 text-left font-semibold text-foreground">Group</th>
//               <th className="px-4 py-3 text-left font-semibold text-foreground">AI Models</th>
//               <th className="px-4 py-3 text-center font-semibold text-foreground">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {cameras.map((cam, idx) => (
//               <tr key={cam.id || cam.camera_id} className={`border-b border-border transition-colors hover:bg-muted/30 ${idx % 2 === 0 ? 'bg-card' : 'bg-muted/5'}`}>
//                 <td className="px-4 py-3"><input type="checkbox" checked={selectedIds.has(cam.id || cam.camera_id)} onChange={() => onToggleSelect(cam.id || cam.camera_id)} className="h-4 w-4 rounded border-border text-brand focus:ring-brand" /></td>                <td className="px-4 py-3 text-muted-foreground">{idx + 1}</td>
//                 <td className="px-4 py-3">
//                   <div className="flex items-center gap-2">
//                     <VideoCameraIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
//                     <span className="font-medium text-foreground truncate max-w-xs">{cam.name}</span>
//                   </div>
//                 </td>
//                 <td className="px-4 py-3"><span className="font-mono text-[11px] text-muted-foreground">{(cam.id || cam.camera_id)?.slice(0, 8)}</span></td>
//                 <td className="px-4 py-3">
//                   {(() => {
//                     const ls = derivePipelineStatus(cam); const cls = statusBadgeClasses(ls); return (
//                       <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${cls.badge}`}>
//                         <span className={`h-1.5 w-1.5 rounded-full ${cls.dot}`} />
//                         {ls}
//                       </span>
//                     );
//                   })()}
//                 </td>
//                 <td className="px-4 py-3 text-muted-foreground">{cam.ip_address || 'N/A'}</td>
//                 <td className="px-4 py-3"><span className="font-mono text-[11px] text-muted-foreground truncate block max-w-[200px]" title={cam.rtsp_url}>{cam.rtsp_url || 'N/A'}</span></td>
//                 <td className="px-4 py-3"><span className="font-mono text-[11px] text-muted-foreground truncate block max-w-[200px]" title={cam.sub_stream_rtsp_url}>{cam.sub_stream_rtsp_url || 'N/A'}</span></td>
//                 <td className="px-4 py-3">
//                   {cam.groups?.length > 0 ? (
//                     <div className="flex flex-wrap gap-1">
//                       {cam.groups.slice(0, 2).map((g: any) => (
//                         <span key={g.id} className="inline-block rounded-md bg-brand/10 px-2 py-0.5 text-[11px] font-medium text-brand">{g.name}</span>
//                       ))}
//                     </div>
//                   ) : <span className="text-muted-foreground">—</span>}
//                 </td>
//                 <td className="px-4 py-3">
//                   {(() => {
//                     const camId = cam.id || cam.camera_id;
//                     const modelNames = cameraModelMap[camId] || [];
//                     if (modelNames.length === 0) return <span className="text-muted-foreground">—</span>;
//                     return (
//                       <div className="flex flex-wrap gap-1 items-center">
//                         {modelNames.slice(0, 3).map((name: string, i: number) => {
//                           const isHeatmap = name.toLowerCase().includes('heatmap');
//                           return isHeatmap ? (
//                             <button key={i} onClick={() => navigate(`/nodes/${selectedNode?.nodeId}/cameras/${camId}/heatmap?name=${encodeURIComponent(cam.name || 'Camera')}`)} title="View Heatmap" className="inline-block rounded-md bg-purple-100 px-2 py-0.5 text-[11px] font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 cursor-pointer hover:bg-purple-200 dark:hover:bg-purple-900/50 hover:ring-1 hover:ring-purple-400/50 transition-all">{name} ↗</button>
//                           ) : (
//                             <span key={i} className="inline-block rounded-md bg-sky-100 px-2 py-0.5 text-[11px] font-medium text-sky-700 dark:bg-sky-900/30 dark:text-sky-400">{name}</span>
//                           );
//                         })}
//                         {modelNames.length > 3 && <span className="inline-block rounded-md bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">+{modelNames.length - 3}</span>}
//                       </div>
//                     );
//                   })()}
//                 </td>
//                 <td className="px-4 py-3 text-center">
//                   <div className="relative inline-block">
//                     <button
//                       onClick={() => setOpenMenuId(openMenuId === (cam.id || cam.camera_id) ? null : (cam.id || cam.camera_id))}
//                       className="rounded-lg p-1.5 hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
//                       title="More actions"
//                     >
//                       <EllipsisVerticalIcon className="h-4 w-4" />
//                     </button>
//                     {openMenuId === (cam.id || cam.camera_id) && (
//                       <div className={`absolute right-0 rounded-lg border border-border bg-card shadow-xl z-50 overflow-hidden min-w-max ${idx >= cameras.length - 2 ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
//                         <button
//                           onClick={() => { navigate(`/nodes/${selectedNode?.nodeId}/cameras/${cam.id || cam.camera_id}`); setOpenMenuId(null); }}
//                           className="flex w-full items-center gap-2 px-4 py-2.5 text-foreground hover:bg-muted transition-colors border-b border-border"
//                         >
//                           <EyeIcon className="h-4 w-4" />
//                           <span>View Live</span>
//                         </button>
//                         <button
//                           onClick={() => { onEdit(cam); setOpenMenuId(null); }}
//                           className="flex w-full items-center gap-2 px-4 py-2.5 text-foreground hover:bg-muted transition-colors border-b border-border"
//                         >
//                           <PencilIcon className="h-4 w-4" />
//                           <span>Edit</span>
//                         </button>
//                         <button
//                           onClick={() => { navigate(`/nodes/${selectedNode?.nodeId}/cameras/${cam.id || cam.camera_id}/config`); setOpenMenuId(null); }}
//                           className="flex w-full items-center gap-2 px-4 py-2.5 text-foreground hover:bg-muted transition-colors border-b border-border"
//                         >
//                           <Cog6ToothIcon className="h-4 w-4" />
//                           <span>ONVIF Config</span>
//                         </button>
//                         <button
//                           onClick={() => { onDelete(cam.id || cam.camera_id); setOpenMenuId(null); }}
//                           className="flex w-full items-center gap-2 px-4 py-2.5 text-destructive hover:bg-muted transition-colors"
//                         >
//                           <TrashIcon className="h-4 w-4" />
//                           <span>Delete</span>
//                         </button>
//                       </div>
//                     )}
//                   </div>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//         <div className="border-t border-border bg-muted/30 px-4 py-2 text-[12px] text-muted-foreground">
//           Showing {cameras.length} cameras
//         </div>
//       </div>
//     </>
//   );
// }

function EmptyState({ groupNames = [], onClearFilter }: { groupNames?: string[]; onClearFilter?: () => void }) {
  // A group filter is now a SERVER filter, so an empty page is usually the filter's doing
  // rather than an empty site — say which groups, and offer the way back.
  const filtered = groupNames.length > 0;
  return (
    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
      <p className="text-[15px] font-medium">No cameras found</p>
      <p className="mt-1 text-[13px]">
        {filtered
          ? `No cameras in ${groupNames.map((n) => `"${n}"`).join(', ')} or their sub-groups.`
          : 'Add a camera or run discovery to get started.'}
      </p>
      {filtered && onClearFilter && (
        <button onClick={onClearFilter} className="mt-2 text-[13px] font-medium text-brand hover:underline">
          Clear group filter
        </button>
      )}
    </div>
  );
}

/**
 * Nest a flat group list (rows with parent_id, e.g. from the getTree CTE) into a
 * parent→children tree. Returns the list unchanged if it is already nested.
 */
function buildGroupTree(flat: any[]): any[] {
  if (!Array.isArray(flat) || flat.length === 0) return [];
  if (flat[0]?.children) return flat;
  const map: Record<string, any> = {};
  const roots: any[] = [];
  for (const g of flat) map[g.id] = { ...g, children: [] };
  for (const g of flat) {
    if (g.parent_id && map[g.parent_id]) map[g.parent_id].children.push(map[g.id]);
    else roots.push(map[g.id]);
  }
  return roots;
}

/**
 * The group tree inside the filter dropdown. `selectedIds` is a Set rather than one id: the
 * filter takes several groups, so each row carries its own checkbox — with one highlighted row
 * there was nowhere to read "is this one also in the filter?".
 */
function renderGroupTree(groups: any[], selectedIds: Set<string>, onSelect: (g: any) => void, level = 0) {
  return groups.map((g: any) => {
    const selected = selectedIds.has(g.id);
    return (
      <div key={g.id}>
        <button
          onClick={() => onSelect(g)}
          aria-pressed={selected}
          className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] transition-colors ${selected ? 'bg-brand/10 font-semibold text-brand' : 'hover:bg-muted text-foreground'
            }`}
          style={{ paddingLeft: `${level * 16 + 8}px` }}
        >
          <span className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors ${
            selected ? 'border-brand bg-brand text-white' : 'border-border'
          }`}>
            {selected && <CheckIcon className="h-3 w-3" />}
          </span>
          <FolderIcon className="h-4 w-4 text-yellow-400 flex-shrink-0" />
          <span className="truncate">{g.name}</span>
        </button>
        {g.children && renderGroupTree(g.children, selectedIds, onSelect, level + 1)}
      </div>
    );
  });
}